import { useState, useEffect } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Save, RotateCcw, Plus, Trash2 } from "lucide-react";

/**
 * Editor for list-of-items sections (stats, features, pillars, socials).
 * itemFields = [{ key, label, type: 'text'|'textarea' }]
 */
export default function ListSectionEditor({ title, sectionKey, defaults, itemFields, listKey = "items", addLabel = "Add Item" }) {
  const [items, setItems] = useState(defaults[listKey] || []);
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/site-content/${sectionKey}`);
        setItems((data.data || {})[listKey] || defaults[listKey] || []);
      } catch { setItems(defaults[listKey] || []); }
      setLoaded(true);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sectionKey]);

  const updateItem = (i, k, v) => {
    const c = [...items]; c[i] = { ...c[i], [k]: v }; setItems(c);
  };
  const remove = (i) => { const c = [...items]; c.splice(i, 1); setItems(c); };
  const add = () => {
    const empty = {}; itemFields.forEach(f => { empty[f.key] = ""; });
    setItems([...items, empty]);
  };
  const save = async () => {
    setSaving(true);
    try {
      await api.post(`/site-content/${sectionKey}`, { data: { [listKey]: items } });
      toast.success(`${title} saved.`);
    } catch { toast.error("Save failed."); }
    setSaving(false);
  };
  const reset = async () => {
    if (!confirm("Reset to defaults?")) return;
    await api.post(`/site-content/${sectionKey}`, { data: {} });
    setItems(defaults[listKey] || []);
    toast.success("Reset.");
  };

  if (!loaded) return <div className="card-light p-6 text-neutral-400">Loading…</div>;
  return (
    <div className="card-light p-6 space-y-5" data-testid={`list-editor-${sectionKey}`}>
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <div className="label-tag" style={{ color: "#000" }}>{sectionKey}</div>
          <div className="font-heading uppercase font-bold text-xl">{title}</div>
        </div>
        <div className="flex gap-2">
          <button onClick={reset} disabled={saving} className="btn-ghost-dark !py-2 !px-4 text-sm" data-testid={`reset-${sectionKey}`}><RotateCcw size={14}/>Reset</button>
          <button onClick={save} disabled={saving} className="btn-gold !py-2 !px-4 text-sm" data-testid={`save-${sectionKey}`}><Save size={14}/>{saving?"Saving…":"Save"}</button>
        </div>
      </div>
      <div className="space-y-3">
        {items.map((it, i) => (
          <div key={i} className="border border-black/10 p-4 rounded-none" data-testid={`item-${sectionKey}-${i}`}>
            <div className="grid md:grid-cols-2 gap-3">
              {itemFields.map(f => (
                <div key={f.key}>
                  <label className="label-tag" style={{ color: "#000" }}>{f.label}</label>
                  {f.type === "textarea" ? (
                    <textarea value={it[f.key] ?? ""} onChange={(e)=>updateItem(i, f.key, e.target.value)} rows={2}
                      className="mt-2 w-full border border-black/15 bg-white p-2 text-sm outline-none focus:border-[color:var(--gold)]"
                      data-testid={`item-${sectionKey}-${i}-${f.key}`}/>
                  ) : (
                    <input value={it[f.key] ?? ""} onChange={(e)=>updateItem(i, f.key, e.target.value)}
                      className="input-sharp mt-1 text-sm" data-testid={`item-${sectionKey}-${i}-${f.key}`}/>
                  )}
                </div>
              ))}
            </div>
            <div className="mt-3 flex justify-end">
              <button onClick={()=>remove(i)} className="text-red-500 text-xs flex items-center gap-1" data-testid={`remove-${sectionKey}-${i}`}><Trash2 size={12}/> Remove</button>
            </div>
          </div>
        ))}
      </div>
      <button onClick={add} className="btn-ghost-dark !py-2 !px-4 text-sm" data-testid={`add-${sectionKey}`}><Plus size={14}/>{addLabel}</button>
    </div>
  );
}
